/**
 * Asset adoption (P2) — bring a project's remote images into the project.
 *
 * An image referenced by an absolute URL is somebody else's file. Every export
 * re-downloads it, an offline source template breaks the export, and the
 * link-checker blocks anything image-shaped that is not on a four-host
 * allowlist. Adoption downloads those bytes once, stores them in the project's
 * own AssetStore, and rewrites the references to `pb-asset://<id>` — after
 * which the images are project data and none of the above applies.
 *
 * SCOPE, and why it is not wider (plan decision D8). `collectImageUrls` scans
 * two inputs: project state, and the template's live CSS. The template CSS is
 * NOT part of state — `migrateState` deletes it unconditionally on every load
 * ("no longer persisted; live-fetched at render time") — so rewriting state can
 * never change a URL that lives in it. Adoption therefore covers state-owned
 * references only, and REPORTS template-owned ones instead of pretending to fix
 * them. Making those adoptable needs a persisted binding consulted by all four
 * render paths (canvas, saved preview, flat export, 11ty); that changes
 * generated output and belongs in its own phase.
 *
 * Downloads go through `fetchAssetThroughCache`, the same path the exporter
 * uses, so adoption inherits P1a's retry/timeout policy and P1b's cache rather
 * than growing a second one.
 */

import { store } from './state.js';
import {
  collectImageUrls,
  fetchAssetThroughCache,
  externalImageTransports
} from '../export/asset-resolver.js';
import { mapWithConcurrency } from '../shared/concurrency.js';
import { makeFinding, SEVERITY } from '../shared/audit-checks.js';

/** Marks an adoption in history so the whole batch is one undo. */
export const ADOPT_ASSETS_LABEL = 'Bring images into project';

/**
 * Split every remote image reference into what adoption can fix and what it
 * cannot. Derived by running the EXISTING collector twice rather than teaching
 * it about provenance: once over state alone, once with the template CSS. The
 * difference is exactly the set that lives outside state.
 *
 * @param {object} state
 * @param {{templateCSS?: string}} [opts]
 * @returns {{stateOwned: string[], templateOwned: string[]}} both sorted
 */
export function analyzeAdoptable(state, { templateCSS = '' } = {}) {
  const remoteOf = (buckets) => new Set([...buckets.cdnUrls, ...buckets.externalUrls]);
  const fromState = remoteOf(collectImageUrls(state, { templateCSS: '' }));
  const withTemplate = remoteOf(collectImageUrls(state, { templateCSS }));
  const templateOwned = [...withTemplate].filter((u) => !fromState.has(u));
  const sort = (a) => a.slice().sort();
  return { stateOwned: sort([...fromState]), templateOwned: sort(templateOwned) };
}

export function makeTemplateOwnedAssetsFinding(urls, sourceDomain = '') {
  const list = [...new Set(urls || [])].sort();
  if (!list.length) return null;
  const owner = sourceDomain || 'the template stylesheet';
  return makeFinding({
    id: `template-css-${list.length}`,
    checkId: 'template-css-owned-assets',
    severity: SEVERITY.CLEANUP,
    title: `${list.length} image${list.length === 1 ? '' : 's'} live in template CSS`,
    detail: `These references cannot be adopted into project state. Rebuild the reference bundle for ${owner} with npm run build:reference-bundles.`,
    location: { target: owner },
    accept: { target: `template:${owner}`, evidence: list.join('\n') }
  });
}

/** Read-only reachability probe that also warms P1b's positive/negative cache. */
export async function probeAssetReachability(
  state,
  { signal = null, limit = 24, saveData = globalThis.navigator?.connection?.saveData } = {}
) {
  if (saveData) return { skipped: 'save-data', checked: 0, failures: [] };
  const { stateOwned } = analyzeAdoptable(state);
  const urls = stateOwned.slice(0, Math.max(0, limit));
  const results = await mapWithConcurrency(urls, 3, async (url) => {
    const out = await fetchAssetThroughCache(
      url,
      externalImageTransports(url),
      { signal },
      // Successful probes warm the exact cache export consumes. A failed
      // readiness probe must not poison the export that immediately follows;
      // generation still gets its own full retry chain.
      { signal, cacheFailures: false }
    );
    return out.ok ? null : { url, reason: out.reason || 'network' };
  });
  return { checked: urls.length, failures: results.filter(Boolean), skipped: null };
}

export function makeReachabilityFindings(failures) {
  return (failures || []).map(({ url, reason }, index) =>
    makeFinding({
      id: `${index}-${url}`,
      checkId: 'asset-reachability',
      severity: SEVERITY.WARNING,
      title: 'A remote image is not reachable yet',
      detail: `${url} could not be warmed (${reason}). Export will retry through the full transport chain.`,
      location: { target: url },
      accept: { target: `asset:${url}`, evidence: reason }
    })
  );
}

/** Stable per-URL asset id. Same hash the exporter uses for asset filenames. */
async function assetIdFor(url) {
  const bytes = new TextEncoder().encode(url);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)]
    .slice(0, 16)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function isImageBlob(blob, url) {
  if (!blob || !(blob.size > 0)) return false;
  if ((blob.type || '').startsWith('image/')) return true;
  return /\.(jpe?g|png|gif|webp|svg|avif)(?:$|[?#])/i.test(String(url || ''));
}

/**
 * Download `urls` and store each in the project's AssetStore.
 *
 * Downloads FIRST, mutates nothing. The caller applies the returned map in one
 * transaction, so a download that fails simply never reaches state — the URL is
 * left exactly as it was and is reported instead.
 *
 * `metaForUrl` overrides the stored asset record's metadata. It exists because
 * the default records `source: <the url we fetched>`, and `save-load.js` copies
 * that verbatim into `assets-meta.json` inside every exported project ZIP. That
 * is correct for a stock photo, whose URL is public attribution — and wrong for
 * anything fetched through an AUTHENTICATED, per-user proxy, where the URL is
 * not attribution but a credentialled path that would ship inside a customer
 * bundle. Callers in that position supply sanitized provenance instead.
 * Omitting it preserves the previous behaviour exactly.
 *
 * `fetchBlob` overrides only the DOWNLOAD step; dedupe, storage, the map and
 * the rollback stay shared. It exists because the default transport chain is
 * built for public image URLs — a bare `fetch` with no credentials, falling
 * back to the export asset PROXY — and neither is correct for a same-origin,
 * session-authenticated route: the bare fetch would 401, and the proxy fallback
 * would post an authenticated internal path to a general-purpose fetcher.
 * Callers on such a route supply their own authed transport. Omitting it
 * preserves the previous behaviour exactly.
 *
 * @param {(url: string, blob: Blob) => object} [opts.metaForUrl]
 * @param {(url: string, opts: {signal: AbortSignal|null}) =>
 *   Promise<{ok: boolean, blob?: Blob, reason?: string}>} [opts.fetchBlob]
 * @returns {Promise<{map: Map<string,string>, failures: Array<{url, reason}>}>}
 */
export async function adoptAssets(
  urls,
  { signal = null, onProgress = null, concurrency = 6, metaForUrl = null, fetchBlob = null } = {}
) {
  const list = [...new Set(urls || [])].sort();
  const map = new Map();
  const failures = [];
  const downloaded = [];
  let done = 0;

  const results = await mapWithConcurrency(list, concurrency, async (url) => {
    if (signal?.aborted) throw new DOMException('Adoption aborted', 'AbortError');
    const out = fetchBlob
      ? await fetchBlob(url, { signal })
      : await fetchAssetThroughCache(url, externalImageTransports(url), { signal }, { signal });
    done += 1;
    if (onProgress) onProgress({ total: list.length, done, url });
    return { url, out };
  });

  for (const r of results) {
    if (!r) continue;
    const { url, out } = r;
    if (!out.ok || !isImageBlob(out.blob, url)) {
      failures.push({ url, reason: out.ok ? 'invalid-mime' : out.reason });
      continue;
    }
    downloaded.push({ url, blob: out.blob });
  }

  // Adoption is all-or-nothing. A partial rewrite is especially deceptive for
  // srcset: one density may become project-owned while another still depends
  // on the source site. Download every byte first, then persist only when the
  // entire requested set is available.
  if (failures.length > 0) return { map, failures };

  const newlyStored = [];
  try {
    for (const { url, blob } of downloaded) {
      const id = await assetIdFor(url);
      const existing = await store.assets.get(id);
      if (!existing) {
        const meta = metaForUrl
          ? metaForUrl(url, blob)
          : {
              filename: url.split('/').pop()?.split('?')[0] || 'image',
              mime: blob.type || 'image/jpeg',
              // Provenance: where these bytes came from, kept for attribution.
              source: url
            };
        await store.assets.storeWithId(id, blob, meta);
        newlyStored.push(id);
      }
      map.set(url, `pb-asset://${id}`);
    }
  } catch (err) {
    // AssetStore has no multi-record transaction API. Roll back only ids this
    // call created; a deterministic id that pre-existed belongs to the project.
    await Promise.allSettled(newlyStored.map((id) => store.assets.delete(id)));
    throw err;
  }
  return { map, failures };
}

/** Replace every occurrence of a mapped URL inside one HTML string. */
function rewriteHtml(html, map) {
  if (typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return html;
  let touched = false;

  const swapAttr = (el, attr) => {
    const value = el.getAttribute(attr);
    if (!value) return;
    const next = map.get(value.trim());
    if (!next) return;
    el.setAttribute(attr, next);
    touched = true;
  };
  const swapSrcset = (el) => {
    const value = el.getAttribute('srcset');
    if (!value) return;
    let changed = false;
    const next = value
      .split(',')
      .map((candidate) => {
        const parts = candidate.trim().split(/\s+/);
        const mapped = map.get(parts[0]);
        if (!mapped) return candidate.trim();
        changed = true;
        parts[0] = mapped;
        return parts.join(' ');
      })
      .join(', ');
    if (changed) {
      el.setAttribute('srcset', next);
      touched = true;
    }
  };

  for (const el of root.querySelectorAll('img, picture source, input[type="image"]')) {
    swapAttr(el, 'src');
    swapSrcset(el);
  }
  for (const el of root.querySelectorAll('image')) {
    swapAttr(el, 'href');
    swapAttr(el, 'xlink:href');
  }
  for (const link of root.querySelectorAll('link[href]')) {
    const rel = new Set(
      String(link.getAttribute('rel') || '')
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
    );
    const isImagePreload = rel.has('preload') && link.getAttribute('as')?.toLowerCase() === 'image';
    if (isImagePreload || rel.has('icon') || rel.has('apple-touch-icon')) swapAttr(link, 'href');
  }
  for (const meta of root.querySelectorAll('meta[content]')) {
    const key = String(
      meta.getAttribute('property') || meta.getAttribute('name') || ''
    ).toLowerCase();
    if (key === 'og:image' || key === 'twitter:image' || key === 'twitter:image:src') {
      swapAttr(meta, 'content');
    }
  }
  for (const el of root.querySelectorAll(
    '[poster], [data-src], [data-poster], [data-placeholder], [data-bg], [data-bg-webp], [data-bg-jpeg]'
  )) {
    for (const attr of [
      'poster',
      'data-src',
      'data-poster',
      'data-placeholder',
      'data-bg',
      'data-bg-webp',
      'data-bg-jpeg'
    ]) {
      swapAttr(el, attr);
    }
  }
  for (const el of root.querySelectorAll('[style]')) {
    const before = el.getAttribute('style') || '';
    const after = rewriteCss(before, map);
    if (after !== before) {
      el.setAttribute('style', after);
      touched = true;
    }
  }
  for (const styleEl of root.querySelectorAll('style')) {
    const before = styleEl.textContent || '';
    const after = rewriteCss(before, map);
    if (after !== before) {
      styleEl.textContent = after;
      touched = true;
    }
  }
  return touched ? root.innerHTML : html;
}

// Mirrors the collector's own CSS_URL_RE in asset-resolver.js. A url() swap is a
// token substitution, not CSS parsing — the repo's no-regex-for-CSS rule is
// about extracting structure, and the collector these URLs came from uses this
// exact shape.
const CSS_URL_RE = /url\((['"]?)([^"')]+)\1\)/gi;

/** Replace every occurrence of a mapped URL inside one CSS string. */
function rewriteCss(css, map) {
  if (typeof css !== 'string' || !css) return css;
  return css.replace(CSS_URL_RE, (whole, quote, url) => {
    const next = map.get(String(url).trim());
    return next ? `url(${quote}${next}${quote})` : whole;
  });
}

/**
 * Compute every state update that adopting `map` implies. PURE — returns update
 * records for the caller to apply in one transaction.
 *
 * @returns {Array<{path: string, value: any}>}
 */
export function rewriteAdoptedUrls(state, map) {
  const updates = [];
  if (!state || !map || map.size === 0) return updates;

  const section = (sec, path) => {
    if (!sec) return;
    if (typeof sec.html === 'string') {
      const next = rewriteHtml(sec.html, map);
      if (next !== sec.html) updates.push({ path: `${path}.html`, value: next });
    }
    if (typeof sec.css === 'string') {
      const next = rewriteCss(sec.css, map);
      if (next !== sec.css) updates.push({ path: `${path}.css`, value: next });
    }
  };

  section(state.sharedSections?.navigation, 'sharedSections.navigation');
  section(state.sharedSections?.footer, 'sharedSections.footer');
  (state.pages || []).forEach((page, pageIndex) => {
    (page.sections || []).forEach((sec, sectionIndex) => {
      section(sec, `pages.${pageIndex}.sections.${sectionIndex}`);
    });
    const og = page?.seo?.ogImage;
    if (og && map.has(og)) {
      updates.push({ path: `pages.${pageIndex}.seo.ogImage`, value: map.get(og) });
    }
  });

  for (const key of ['headCode', 'bodyCode']) {
    if (typeof state[key] !== 'string' || !state[key]) continue;
    const next = rewriteHtml(state[key], map);
    if (next !== state[key]) updates.push({ path: key, value: next });
  }

  const pwa = state.pwaInstall;
  if (pwa && typeof pwa === 'object') {
    if (typeof pwa.appIcon === 'string' && map.has(pwa.appIcon)) {
      updates.push({ path: 'pwaInstall.appIcon', value: map.get(pwa.appIcon) });
    }
    for (const [index, shot] of (pwa.screenshots || []).entries()) {
      if (typeof shot?.asset === 'string' && map.has(shot.asset)) {
        updates.push({ path: `pwaInstall.screenshots.${index}.asset`, value: map.get(shot.asset) });
      }
    }
  }

  return updates;
}
